import DataArrayIcon from '@mui/icons-material/DataArray';
import { Box, IconButton, SxProps, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Editor, IAllProps as ITinymceProps } from '@tinymce/tinymce-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RichEditorEventsEnum } from '../../../../constants/shell';
import { TAG_PICKER_AVAILABLE_TAGS } from '../../../../constants/templates';
import { TinyMCEEditor } from '../../../../types/shell';
import { isLightColor } from '../../../../utils/ui';
import { TagPickerFloatButton } from '../TagPickerFloatButton';

import { editorTextStyles, StyledEditorContainer } from './RichEditor.styles';

const TAG_CHIP_CLASS = 'rich-editor-tag-chip';

/**
 * Build the HTML for a non-editable tag chip that TinyMCE treats as an atomic unit.
 */
const buildTagChipHtml = (tag: string): string =>
  `<span class="${TAG_CHIP_CLASS}" contenteditable="false" data-tag="${tag}">${tag}</span>`;

/**
 * Regex that matches any known `[Tag.Name]` token inside HTML content.
 * Only matches tags from the TAG_PICKER_AVAILABLE_TAGS list.
 */
const buildBracketTagRegex = (): RegExp => {
  const escaped = TAG_PICKER_AVAILABLE_TAGS.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`\\[(${escaped.join('|')})\\]`, 'g');
};

const BRACKET_TAG_REGEX = buildBracketTagRegex();

const ALIGNED_LIST_ITEM_STYLE = `
  li[style*="text-align: center"],
  li[style*="text-align: right"] {
    list-style-position: inside;
  }
`;

const PROGRAMMATIC_COMMANDS = new Set(['mcefocus']);

const isProgrammaticCommand = (command?: string) => PROGRAMMATIC_COMMANDS.has((command ?? '').toLowerCase());

/** Convert `[Tag]` plain-text tokens → chip spans (for loading into editor). */
const tagsToChips = (html: string): string => html.replace(BRACKET_TAG_REGEX, (_, tag) => buildTagChipHtml(tag));

/** Convert chip spans → `[Tag]` plain-text tokens (for storage / onChange). */
const chipsToTags = (html: string): string =>
  html.replace(new RegExp(`<span[^>]*data-tag="([^"]+)"[^>]*>[^<]*</span>`, 'g'), (_, tag) => `[${tag}]`);

// `...props` is spread into TinyMCE's `init`, so editor options are accepted as top-level props.
type IEditorInitOptions = NonNullable<ITinymceProps['init']>;

interface IProps extends ITinymceProps, Omit<IEditorInitOptions, keyof ITinymceProps> {
  value?: string;
  onInputChange?: (content: string) => void;
  sx?: SxProps<Theme>;
  height?: number;
  hasToolbar?: boolean;
  hasColorPicker?: boolean;
  hasTagPicker?: boolean;
  hasAdaptiveBackground?: boolean;
  dataAction?: string;
  error?: boolean;
  helperText?: string;
  /** Seed the editor once and let it own its content, for consumers that echo each edit back. */
  isUncontrolled?: boolean;
  onEditorInit?: (editor: TinyMCEEditor) => void;
  hasListItemAlignment?: boolean;
}

export const RichEditor = ({
  value,
  onInputChange,
  sx,
  height = 150,
  hasToolbar = true,
  hasColorPicker = false,
  hasTagPicker = false,
  hasAdaptiveBackground = false,
  dataAction = '',
  error = false,
  helperText,
  disabled = false,
  isUncontrolled = false,
  onEditorInit,
  hasListItemAlignment = false,
  ...props
}: IProps) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);

  const editorRef = useRef<TinyMCEEditor | null>(null);
  const bookmarkRef = useRef<ReturnType<TinyMCEEditor['selection']['getBookmark']> | null>(null);
  const insertTextHandlerRef = useRef<((text: string) => void) | undefined>();
  const isTagPickerOpenRef = useRef(false);

  // Convert [Tag] bracket tokens to chip spans for display in the editor
  const editorValue = useMemo(() => (hasTagPicker && value ? tagsToChips(value) : value), [value, hasTagPicker]);

  const handleTagClick = useCallback((tag: string) => {
    if (insertTextHandlerRef.current) {
      insertTextHandlerRef.current(tag);
    }
  }, []);

  const [tagPickerAnchorEl, setTagPickerAnchorEl] = useState<HTMLElement | null>(null);

  const handleTagPickerOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    // Snapshot the live caret before the popover steals focus. Clicking the button blurs the
    // editor, and the blur handler's bookmark can resolve to the body start once focus has left,
    // so each inserted tag would land at the same spot and overwrite the previous one. Capturing
    // here keeps every insertion at the real caret position.
    if (editorRef.current) {
      bookmarkRef.current = editorRef.current.selection.getBookmark(2, true);
    }
    isTagPickerOpenRef.current = true;
    setTagPickerAnchorEl(event.currentTarget);
  }, []);

  const handleTagPickerClose = useCallback(() => {
    isTagPickerOpenRef.current = false;
    setTagPickerAnchorEl(null);
  }, []);

  const toolbarSettings = useMemo(() => {
    if (hasToolbar) {
      const colorButton = hasColorPicker ? 'forecolor ' : '';
      return `${colorButton}bold italic underline | alignleft aligncenter alignright | bullist numlist | link image`;
    }
    return false;
  }, [hasToolbar, hasColorPicker]);

  const styleRef = useRef<HTMLStyleElement | null>(null);

  const handleEditorInitStyles = useCallback(() => {
    if (styleRef.current) {
      styleRef.current.remove();
    }
    const style = document.createElement('style');
    style.textContent = `
      .tox-toolbar__overflow .tox-tbtn svg {
        fill: ${theme.palette.action.active} !important;
      }
    `;
    document.head.appendChild(style);
    styleRef.current = style;
  }, [theme.palette.action.active]);

  useEffect(() => {
    return () => {
      styleRef.current?.remove();
    };
  }, []);

  // Find the text color covering the most characters in the editor body. Tag chips are skipped
  // since they render on their own chip background.
  const getDominantTextColor = useCallback(() => {
    const body = editorRef.current?.getBody();
    const view = body?.ownerDocument?.defaultView;
    if (!body || !view) {
      return null;
    }

    const colorWeights = new Map<string, number>();
    const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const textLength = node.textContent?.trim().length ?? 0;
      const parentElement = node.parentElement;
      if (textLength && parentElement && !parentElement.closest(`.${TAG_CHIP_CLASS}`)) {
        const cssColor = view.getComputedStyle(parentElement).color;
        colorWeights.set(cssColor, (colorWeights.get(cssColor) ?? 0) + textLength);
      }
      node = walker.nextNode();
    }

    let dominantColor: string | null = null;
    let dominantWeight = 0;
    colorWeights.forEach((weight, cssColor) => {
      if (weight > dominantWeight) {
        dominantWeight = weight;
        dominantColor = cssColor;
      }
    });
    return dominantColor;
  }, []);

  // The editor background is binary: black behind light text, white behind dark text, so the text
  // the user is typing always stays visible. The editor body lives in the TinyMCE iframe outside
  // the React tree, so the style is applied directly instead of going through state.
  const updateAdaptiveBackgroundColor = useCallback(() => {
    const bodyStyle = editorRef.current?.getBody()?.style;
    if (!bodyStyle) {
      return;
    }

    if (!hasAdaptiveBackground) {
      bodyStyle.removeProperty('background-color');
      return;
    }

    const dominantTextColor = getDominantTextColor();
    const hasLightText = !!dominantTextColor && isLightColor(dominantTextColor);
    bodyStyle.setProperty('background-color', hasLightText ? theme.palette.common.black : theme.palette.common.white);
  }, [hasAdaptiveBackground, getDominantTextColor, theme.palette.common.black, theme.palette.common.white]);

  // content_style only applies on init, so background updates go directly to the editor body
  useEffect(() => {
    if (isEditorInitialized) {
      updateAdaptiveBackgroundColor();
    }
  }, [isEditorInitialized, updateAdaptiveBackgroundColor]);

  // Track the last value emitted to the parent to avoid no-op updates from TinyMCE HTML normalization
  const lastEmittedRef = useRef<string | undefined>(value);
  useEffect(() => {
    lastEmittedRef.current = value;
  }, [value]);

  // TinyMCE normalizes the seeded content during init (plain text becomes `<div>text</div>` under
  // `forced_root_block`), which fires a change with no user action and would mark the form dirty on
  // open. Emits stay suppressed until a real interaction.
  const hasUserInteractedRef = useRef(false);
  const isInitSettledRef = useRef(false);

  // Held in a ref so init stays a one-time callback and never re-subscribes on a new prop identity.
  const onEditorInitRef = useRef(onEditorInit);
  useEffect(() => {
    onEditorInitRef.current = onEditorInit;
  }, [onEditorInit]);

  const handleEditorChange = useCallback(
    (content: string, editor: TinyMCEEditor) => {
      updateAdaptiveBackgroundColor();

      if (!onInputChange) {
        return;
      }

      // Convert chip spans back to [Tag] bracket format for storage
      const normalized = hasTagPicker ? chipsToTags(content) : content;

      // Skip no-op updates caused by TinyMCE normalizing our chip HTML on load
      if (normalized === lastEmittedRef.current) {
        return;
      }

      lastEmittedRef.current = normalized;

      if (!isInitSettledRef.current || !hasUserInteractedRef.current) {
        return;
      }

      onInputChange(normalized);
    },
    [onInputChange, hasTagPicker, updateAdaptiveBackgroundColor],
  );

  const consumerInitInstanceCallbackRef = useRef(props.init_instance_callback);
  useEffect(() => {
    consumerInitInstanceCallbackRef.current = props.init_instance_callback;
  }, [props.init_instance_callback]);

  const handleInitInstance = useCallback((editor: TinyMCEEditor) => {
    isInitSettledRef.current = true;
    const consumerCallback = consumerInitInstanceCallbackRef.current;
    if (typeof consumerCallback === 'function') {
      consumerCallback(editor);
    }
  }, []);

  const handleEditorInit = useCallback((_: unknown, editor: TinyMCEEditor) => {
    editorRef.current = editor;
    setIsEditorInitialized(true);
    onEditorInitRef.current?.(editor);

    const markInteracted = () => {
      hasUserInteractedRef.current = true;
    };
    [
      RichEditorEventsEnum.Input,
      RichEditorEventsEnum.KeyDown,
      RichEditorEventsEnum.Paste,
      RichEditorEventsEnum.Cut,
      RichEditorEventsEnum.Drop,
    ].forEach((event) => editor.on(event, markInteracted));
    editor.on(RichEditorEventsEnum.BeforeExecCommand, (event?: { command?: string }) => {
      if (!isProgrammaticCommand(event?.command)) {
        markInteracted();
      }
    });

    editor.on(RichEditorEventsEnum.Focus, () => {
      setIsFocused(true);
    });

    // Save cursor position on blur, unless the tag picker is driving the blur. While the picker is
    // open the caret snapshot from handleTagPickerOpen (and each subsequent insert) is authoritative;
    // a blur bookmark taken after focus has left the editor can collapse to the body start and cause
    // inserted tags to overwrite one another.
    editor.on(RichEditorEventsEnum.Blur, () => {
      setIsFocused(false);
      if (!isTagPickerOpenRef.current) {
        bookmarkRef.current = editor.selection.getBookmark(2, true);
      }
    });

    if (insertTextHandlerRef) {
      insertTextHandlerRef.current = (text: string) => {
        if (editorRef.current) {
          // Restore cursor position if bookmark exists
          if (bookmarkRef.current) {
            editorRef.current.selection.moveToBookmark(bookmarkRef.current);
          }

          // A chip is `user-select: all`, so when the caret sits on one the restored selection spans
          // the whole chip. insertContent replaces the selection, which would overwrite that chip, so
          // collapse to the end of the selection first — the new tag then lands right after it.
          editorRef.current.selection.collapse(false);

          const tag = text.startsWith('[') && text.endsWith(']') ? text.slice(1, -1) : text;
          editorRef.current.insertContent(buildTagChipHtml(tag));
          editorRef.current.focus();
          // Update bookmark after insertion
          bookmarkRef.current = editorRef.current.selection.getBookmark(2, true);
        }
      };
    }
  }, []);

  const editorContent = (
    <StyledEditorContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-action={`${dataAction}_editor`}
      isFocused={isFocused}
      isHovered={isHovered}
      hasToolbar={hasToolbar}
      error={error}
      sx={sx}
    >
      <Box sx={{ position: 'relative' }}>
        <Editor
          tinymceScriptSrc="/static/libs/tinymce/tinymce.min.js"
          licenseKey="gpl"
          {...(isUncontrolled ? { initialValue: editorValue } : { value: editorValue })}
          disabled={disabled}
          onInit={handleEditorInit}
          onEditorChange={handleEditorChange}
          init={{
            resize: false,
            menubar: false,
            statusbar: false,
            height,
            max_height: 400,
            borderColor: theme.palette.divider,
            plugins: 'lists link image table',
            toolbar: toolbarSettings,
            content_style: `
                        body {
                            ${editorTextStyles(theme)}
                        }
                        .${TAG_CHIP_CLASS} {
                            display: inline-flex;
                            align-items: center;
                            height: 24px;
                            padding: 0 10px;
                            margin: 0 2px;
                            font-size: 13px;
                            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
                            line-height: 24px;
                            border-radius: 16px;
                            background-color: ${grey[200]};
                            color: ${theme.palette.common.black};
                            user-select: all;
                            cursor: default;
                            vertical-align: middle;
                        }
                        ${hasListItemAlignment ? ALIGNED_LIST_ITEM_STYLE : ''}
                    `,
            forced_root_block: 'div',
            // Let formats (e.g. forecolor) wrap non-editable tag chips so tokens inherit the applied color
            format_noneditable_selector: `span.${TAG_CHIP_CLASS}`,
            setup: (editor: TinyMCEEditor) => {
              // set the color of the toolbar icons
              editor.on(RichEditorEventsEnum.Init, handleEditorInitStyles);
            },
            ...props,
            init_instance_callback: handleInitInstance,
            // Security-critical settings must come after ...props to prevent overrides
            valid_elements:
              'p[style],br,strong/b,em/i,u,s,sub,sup,h1[style],h2[style],h3[style],h4[style],h5[style],h6[style],' +
              `ul,ol,${
                hasListItemAlignment ? 'li[style]' : 'li'
              },a[href|target|rel],img[src|alt|width|height],table[border|cellpadding|cellspacing],thead,tbody,tr,td[colspan|rowspan],th[colspan|rowspan],` +
              'span[style|class|contenteditable|data-tag],blockquote,pre,code,hr,div[style]',
            invalid_elements: 'script,iframe,object,embed,form',
            valid_schemes: 'http,https,mailto',
          }}
        />
        {hasTagPicker && (
          <Tooltip title="Variables" placement="right" arrow>
            <IconButton
              onClick={handleTagPickerOpen}
              aria-label="Insert variable"
              sx={{
                width: 30,
                height: 30,
                p: 0.625,
                position: 'absolute',
                bottom: 8,
                left: 8,
                zIndex: 1,
                backgroundColor: grey[200],
                '&:hover': {
                  backgroundColor: grey[200],
                },
              }}
            >
              <DataArrayIcon fontSize="small" sx={{ color: `${theme.palette.action.active}` }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {error && helperText && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: 'error.main',
          }}
        >
          {helperText}
        </Typography>
      )}
    </StyledEditorContainer>
  );

  return (
    <>
      {editorContent}
      {hasTagPicker && (
        <TagPickerFloatButton
          anchorEl={tagPickerAnchorEl}
          onClose={handleTagPickerClose}
          onTagClick={(tag) => {
            handleTagClick(tag);
            handleTagPickerClose();
          }}
        />
      )}
    </>
  );
};
